import React, { useState, useEffect, useCallback, useRef } from "react";
import HeartIcon from "../../assets/icons/HeartIcon";
import ArrowIconBody from "../../assets/icons/ArrowIconBody";

interface GalleryProps {
  images: string[];
  onFavoriteToggle?: () => void;
  isFavorite?: boolean;
}

const Gallery: React.FC<GalleryProps> = ({ images, onFavoriteToggle, isFavorite }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [thumbnailOffset, setThumbnailOffset] = useState(0);
  const [thumbnailsVisible, setThumbnailsVisible] = useState(5);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateThumbnailsVisible = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const thumbnailWidth = 131 + 8;
        const visibleCount = Math.floor(containerWidth / thumbnailWidth);
        setThumbnailsVisible(visibleCount);
      }
    };

    updateThumbnailsVisible();
    window.addEventListener("resize", updateThumbnailsVisible);
    return () => window.removeEventListener("resize", updateThumbnailsVisible);
  }, []);

  const nextImage = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const closeModal = () => {
    setModalOpen(false);
  };

  const scrollThumbnails = (direction: "left" | "right") => {
    if (direction === "left") {
      setThumbnailOffset((prev) => Math.max(prev - 1, 0));
    } else {
      setThumbnailOffset((prev) =>
        Math.min(prev + 1, images.length - thumbnailsVisible)
      );
    }
  };

  return (
    <div>
      {/* Главное изображение */}
      <div className="relative">
        <img
          src={images[activeIndex]}
          alt="Главное изображение"
          className="w-full h-[455px] object-contain cursor-zoom-in rounded-[1px]"
          onClick={() => setModalOpen(true)}
        />

        {/* Иконка избранного с условным оформлением */}
        <button
          className={`absolute top-2 left-2 ${isFavorite ? "bg-red-500" : "bg-[#2C2C2C]"} p-[12px] rounded-full shadow`}
          onClick={onFavoriteToggle}
        >
          <HeartIcon className="w-[20px] h-[20px]" />
        </button>
      </div>

      {/* Миниатюры снизу */}
      <div className="flex items-center w-full mt-4">
        <button
          className="mr-2 rotate-180"
          onClick={() => scrollThumbnails("left")}
          disabled={thumbnailOffset === 0}
        >
          <ArrowIconBody stroke={thumbnailOffset === 0 ? "#B3B3B3" : ""} />
        </button>

        <div ref={containerRef} className="flex gap-2 overflow-hidden flex-1">
          {images
            .slice(thumbnailOffset, thumbnailOffset + thumbnailsVisible)
            .map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`Миниатюра ${idx}`}
                className={`w-[131px] h-[131px] object-contain border rounded-[1px] cursor-pointer ${
                  activeIndex === thumbnailOffset + idx ? "border-[#383838]" : ""
                }`}
                onClick={() => setActiveIndex(thumbnailOffset + idx)}
              />
            ))}
        </div>

        <button
          className="ml-2"
          onClick={() => scrollThumbnails("right")}
          disabled={thumbnailOffset + thumbnailsVisible >= images.length}
        >
          <ArrowIconBody stroke={thumbnailOffset + thumbnailsVisible >= images.length ? "#B3B3B3" : ""} />
        </button>
      </div>

      {/* Модальное окно */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center"
          onClick={closeModal}
        >
          <div
            className="relative max-w-4xl w-full px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-white text-2xl"
              onClick={closeModal}
            >
              &times;
            </button>

            <img
              src={images[activeIndex]}
              alt="Fullscreen"
              className="w-full max-h-[80vh] object-contain"
            />

            {images.length > 1 && (
              <button
                className="absolute left-2 top-1/2 transform -translate-y-1/2 p-2 rotate-180"
                onClick={prevImage}
              >
                <ArrowIconBody stroke="#fff" />
              </button>
            )}

            {images.length > 1 && (
              <button
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2"
                onClick={nextImage}
              >
                <ArrowIconBody stroke="#fff" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
